import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Filter, FolderPlus, GripVertical, X } from "lucide-react";

import { WithheldCount } from "@app/components/WithheldCount";
import { cn } from "@app/lib/utils";
import { useScope } from "@app/zustand/scope";
import useMageXStore, {
  type NLChanges,
  type NLFilterCondition,
  type NLSearchBase,
  type NLUnmatched,
} from "@app/zustand/store";
import {
  useCanFilterByPractitioner,
  usePractitionerFilter,
} from "@app/utils/use-practitioner-filter";
import {
  COMPOSITION_ID,
  SINGULARITY_ID,
  sameQuestion,
  usePathwayDashboard,
  type PathwayCard,
} from "@app/zustand/dashboard-pathway";
import type { PathwayDimension, Representation } from "@app/types/dashboard-pathway";
import { valueLabeller } from "@app/utils/value-labels";

import { conditionWeight, isRange, rangeText, selectionKey } from "./conditions";
import { rebased, searchBoard } from "./search";

import CompositionCard from "./CompositionCard";
import DimensionPicker from "./DimensionPicker";
import QuestionBar from "./QuestionBar";
import { useQuestionPresets, type QuestionPreset } from "./labels";
import PathwayFilterRail from "./PathwayFilterRail";
import PathwayPatientsModal from "./PathwayPatientsModal";
import { useBoardSync } from "./useBoardSync";
import SingularityCard from "./SingularityCard";
import {
  buildPathwayQuery,
  usePathwayComposition,
  usePathwayDimensions,
  usePathwayFacets,
  usePathwayPractitioners,
  usePathwaySingularity,
  usePathwaySummary,
} from "./usePathway";

/**
 * "Exploration" — the pathway dashboard.
 *
 * Two cards, not seven: a composition and a gap. Everything else the reader wants is one of those
 * two shapes with different dimensions, which is what makes the board learnable — and a filled grid
 * was never an answer anyway.
 *
 * One rule governs the interaction, and it is deliberate: **a click filters the PAGE, not the card.**
 * "Show me only my spondylolistheses" is true of the whole screen; per-card filters create two states
 * that diverge and nobody knows what they are looking at any more.
 */

/** The card « + composition » adds for one dimension. */
const newComposition = (key: string): Omit<PathwayCard, "id"> => ({
  kind: "composition",
  dims: [key],
  mode: "n",
  representation: null,
});

/** What the board counts for its current selection — patients and episodes, always both. */
export interface CohortSelection {
  patients: number;
  episodes: number;
}

export interface PathwayBodyProps {
  /** The relative window token — `7j`, `30j`, `a_venir`… resolved server-side against today. */
  periode?: string | null;
  periodeStart?: string | null;
  periodeEnd?: string | null;
  /** Writes the period back, so a search that names a year narrows the whole page like any filter. */
  onPeriodChange?: (start: string | null, end: string | null) => void;
  /** Writes the relative token back. Separate, because a token is not a frozen pair of dates. */
  onPeriodTokenChange?: (token: string | null) => void;
  /** Conditions the natural-language search already resolved. */
  nlFilters?: NLFilterCondition[];
  /** Terms of that search the board holds no value for. */
  nlUnmatched?: NLUnmatched[];
  /** What that search kept, changed, added and removed of the board's conditions (the server's count). */
  nlChanges?: NLChanges | null;
  /** Clears everything that can narrow this view — conditions, period, and the search itself. */
  onResetAll?: () => void;
  /** The board as it was when that search was asked: what its answer replaces. */
  nlBase?: NLSearchBase;
  /** The board's selection right after it applied that search (`selectionKey`); `null` until it has. */
  nlApplied?: string | null;
  /** Told the selection the board holds once it has applied the search. */
  onSearchApplied?: (key: string) => void;
  /**
   * Opens the cohort dialog. The rail is where a selection is now BUILT, so it is where it is saved —
   * with the counts this board shows, the only ones that describe what the cohort will hold.
   */
  onSaveCohort?: (selection: CohortSelection) => void;
  /** The host's own actions on the selection (the dashboard's export), drawn beside the filter toggle. */
  actions?: ReactNode;
  /**
   * The reader is inspecting a speciality rather than practising in it — the "vue globale" persona.
   *
   * Decided HERE and not on the server, and that is not a shortcut. A previewed role is honoured by
   * the backend for ADMIN sessions only — it may narrow what is shown, never widen it — and the demo
   * account is a `medecin`, so the header it sends is dropped and `ctx.me` stays the demo doctor
   * whatever the toggle says. The preview is a front-end simulation of a view; the gate that follows
   * from it belongs on the same side. The server-side rule stays for a REAL global account.
   */
  isGlobalView?: boolean;
  /**
   * Field keys the practitioner switched off in their own preferences. Pruned here rather than on
   * the server: the payload is shared across a speciality, so filtering it upstream would hide one
   * doctor's card from all their colleagues.
   */
  hiddenFieldKeys?: Set<string>;
  /**
   * Field keys the practitioner switched on: they bring back an axis the speciality hides by default
   * (`PathwayDimension.shown`).
   */
  shownFieldKeys?: Set<string>;
  /**
   * A catalogue cohort, when this board is one (PT-96). Sent to every route: the server replays the
   * cohort in its owner's context and decides what may be named. Such a board renders under its own
   * store — see `createCohortPathwayStore` — and never syncs a layout.
   */
  cohortId?: string;
  /** A laboratory dataset, when this board is one — scoped and rendered exactly like a cohort. */
  datasetId?: string;
}

export default function PathwayBody({
  periode,
  periodeStart,
  periodeEnd,
  onPeriodChange,
  onPeriodTokenChange,
  nlFilters,
  nlUnmatched,
  nlChanges,
  onResetAll,
  nlBase,
  nlApplied,
  onSearchApplied,
  onSaveCohort,
  actions,
  isGlobalView = false,
  hiddenFieldKeys,
  shownFieldKeys,
  cohortId,
  datasetId,
}: PathwayBodyProps) {
  const { t, i18n } = useLingui();
  const scope = useScope((s) => s.scope);

  const cards = usePathwayDashboard((s) => s.cards);
  const conditions = usePathwayDashboard((s) => s.conditions);
  const pinned = usePathwayDashboard((s) => s.pinned);
  // The rail is opened from the counter line's single "Filtres" toggle, right above where it opens.
  const railOpen = usePathwayDashboard((s) => s.railOpen);
  const setRailOpen = usePathwayDashboard((s) => s.setRailOpen);
  const patchCard = usePathwayDashboard((s) => s.patchCard);
  const addCard = usePathwayDashboard((s) => s.addCard);
  const removeCard = usePathwayDashboard((s) => s.removeCard);
  const setConditions = usePathwayDashboard((s) => s.setConditions);
  const toggleCondition = usePathwayDashboard((s) => s.toggleCondition);
  const setRange = usePathwayDashboard((s) => s.setRange);
  const clearConditions = usePathwayDashboard((s) => s.clearConditions);
  const moveCard = usePathwayDashboard((s) => s.moveCard);
  const pinQuestion = usePathwayDashboard((s) => s.pinQuestion);
  const unpinQuestion = usePathwayDashboard((s) => s.unpinQuestion);

  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  /** Index of the card being dragged. Null when nothing is. */
  const [dragging, setDragging] = useState<number | null>(null);
  /** Dimensions the last natural-language search named — offered as a card as well as a filter. */
  const [searchedDims, setSearchedDims] = useState<string[]>([]);
  /** Columns that search named and this view cannot express. Reported, never dropped in silence. */
  const [ignoredColumns, setIgnoredColumns] = useState<string[]>([]);
  // The search's account of what it kept and changed, until the reader dismisses it — and not again on the
  // way back from a patient page, once it has been read.
  const [changesShown, setChangesShown] = useState(nlApplied == null);

  // A cohort or a dataset: a population of its own, on a store of its own.
  const scoped = Boolean(cohortId || datasetId);
  const { data: catalogue, error: catalogueError } = usePathwayDimensions(
    false,
    cohortId,
    datasetId,
  );
  /* Outside the warehouse's authorised area there is no « Toute la spécialité » and nobody to compare
     with: the server says so on the board's perimeter, and the selector and the card follow. */
  const reuseAuthorised = useScope((s) => s.reuseAuthorised);
  const setReuseAuthorised = useScope((s) => s.setReuseAuthorised);
  useEffect(() => {
    if (catalogue && !scoped) setReuseAuthorised(catalogue.reuse_authorised !== false);
  }, [catalogue, scoped, setReuseAuthorised]);
  // Inside a cohort or a dataset the comparison is that population's own, and the server decides.
  const comparesColleagues = scoped || reuseAuthorised;
  const dimensions = useMemo(
    () =>
      (catalogue?.dimensions ?? []).filter(
        (d) =>
          !hiddenFieldKeys?.has(d.key) &&
          // An open field whose class sits beside it is off by default, until the doctor wants it.
          (d.shown !== false || Boolean(shownFieldKeys?.has(d.key))),
      ),
    [catalogue, hiddenFieldKeys, shownFieldKeys],
  );
  const byKey = useMemo(() => new Map(dimensions.map((d) => [d.key, d])), [dimensions]);
  const composedDims = useMemo(
    () =>
      dimensions
        .filter((d) => cards.some((card) => sameQuestion(card, newComposition(d.key))))
        .map((d) => d.key),
    [dimensions, cards],
  );
  /* Every axis of the catalogue, the ones this reader switched off included: a condition on one is still a
     condition — the rail lists them — and a search keeping it must not drop it as foreign. */
  const allByKey = useMemo(
    () => new Map((catalogue?.dimensions ?? []).map((d) => [d.key, d])),
    [catalogue],
  );
  const axisLabel = useCallback(
    (key: string) => (key === "periode" ? t`période` : (allByKey.get(key)?.label ?? key)),
    [allByKey, t],
  );
  // "laminoplastie (Traitements chirurgicaux)" — named, since a dropped term reads as an answer.
  const unmatchedTerms = useMemo(() => {
    if (!nlUnmatched || nlUnmatched.length === 0) return null;
    return nlUnmatched
      .map((miss) => {
        const axis =
          miss.column === "periode" || allByKey.has(miss.column) ? axisLabel(miss.column) : miss.label;
        return `${miss.terms.join(", ")} (${axis})`;
      })
      .join(" ; ");
  }, [nlUnmatched, allByKey, axisLabel]);
  // « Gardé : Décision · Ajouté : Âge à l'intervention · Retiré : période » — by the server's count.
  const searchChanges = useMemo(() => {
    if (!nlChanges || !changesShown) return null;
    const named = (keys: string[]) => keys.map(axisLabel).join(", ");
    const added = named(nlChanges.added);
    const changed = named(nlChanges.changed);
    const removed = named(nlChanges.removed);
    const kept = named(nlChanges.kept);
    const parts = [
      added ? t`Ajouté : ${added}` : null,
      changed ? t`Modifié : ${changed}` : null,
      removed ? t`Retiré : ${removed}` : null,
      kept ? t`Gardé : ${kept}` : null,
    ].filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [nlChanges, changesShown, axisLabel, t]);
  /* Every value on this board is a term, and every place that SHOWS one draws it through here. Read
     off the whole catalogue rather than the visible dimensions: a field the doctor switched off still
     names the act of a patient in the list. */
  const labelOf = useMemo(
    () => valueLabeller(catalogue?.dimensions),
    [catalogue],
  );
  // The axis the server names a dossier's act from (`get_pathway_patients`).
  const actKey = useMemo(
    () =>
      catalogue?.dimensions.find((d) => !d.native && d.role === "current")
        ?.key ?? null,
    [catalogue],
  );

  /* The arrangement follows the DOCTOR, not the browser. Started only once the catalogue is in:
     hydrating a board before knowing which dimensions exist would paint cards that are about to be
     filtered away. */
  useBoardSync(Boolean(catalogue) && !scoped);

  // A global reader has no patientèle to narrow to, so the perimeter is the speciality whatever the
  // (hidden) scope selector last remembered.
  // Inside a cohort or a dataset the dashboard's scope selector says nothing: it is the population,
  // and the server masks it by whose it is.
  const backendScope =
    isGlobalView || (!scoped && scope === "specialite") ? "specialite" : "own";

  // Admins and MAGE-X reviewers only, and on the reader's own perimeter: a cohort carries the practitioner it
  // was saved with, and a dataset is a named selection (PT-111).
  const canFilterByPractitioner = useCanFilterByPractitioner() && !scoped;
  const storedPractitioner = usePractitionerFilter();
  const practitioner = scoped ? null : storedPractitioner;
  const setPractitionerFilter = useMageXStore((s) => s.setPractitionerFilter);
  const practitioners = usePathwayPractitioners(
    !canFilterByPractitioner || !railOpen,
  );

  const query = useMemo(
    () =>
      buildPathwayQuery(conditions, {
        periode,
        periodeStart,
        periodeEnd,
        scope: backendScope,
        cohortId,
        datasetId,
        praticien: practitioner?.id,
      }),
    [
      conditions,
      periode,
      periodeStart,
      periodeEnd,
      backendScope,
      cohortId,
      datasetId,
      practitioner,
    ],
  );

  const summary = usePathwaySummary(query, !catalogue);
  // The rail's values, and only the rail's: not read while it is closed.
  const facets = usePathwayFacets(query, !catalogue || !railOpen);

  /* The natural-language search is a filter like any other when its column is a dimension we hold.
     Applying it here rather than sending it to the backend keeps ONE filter model on this view: the
     chips, the rail and the search all write the same conditions. */
  useEffect(() => {
    // Once per answer, and only once the board knows its axes. Whether this answer was applied is the
    // dashboard's to say rather than this component's: the board may be left for a patient page while the
    // model answers, and must apply the answer when it comes back. Without that, the effect re-ran on every
    // render — `onPeriodChange` is an inline closure — and re-imposed the search a beat after a reset.
    if (!catalogue || !nlFilters || nlApplied !== null) return;
    const answer = searchBoard(nlFilters, allByKey);
    const current = { conditions, period: { periode, periodeStart, periodeEnd } };
    /* The answer is the WHOLE set, the board's own conditions included: the search was shown them, kept
       what the question refined and dropped what it started over from (PT-146). So it REPLACES the board's
       — merged into them, a search for cimentoplasties kept the previous search's L4-L5 and smokers, and
       said nothing of them. Replaces what it was written against, though: an axis the answer left as it
       found it keeps an edit made while the model answered. */
    const next = nlBase ? rebased(answer, nlBase, current) : answer;
    const { periode: token, periodeStart: start, periodeEnd: end } = next.period;
    if (token) onPeriodTokenChange?.(token);
    else if (start || end) onPeriodChange?.(start ?? null, end ?? null);
    else onPeriodTokenChange?.(null);
    setConditions(next.conditions);
    setIgnoredColumns(answer.ignored);
    setChangesShown(true);
    // A card is offered for what the search added or redefined, not for what it only kept.
    const named = nlChanges ? [...nlChanges.added, ...nlChanges.changed] : Object.keys(next.conditions);
    setSearchedDims(named.filter((key) => key in next.conditions && byKey.has(key)));
    onSearchApplied?.(
      selectionKey(next.conditions, {
        periode: token ?? null,
        periodeStart: token ? null : (start ?? null),
        periodeEnd: token ? null : (end ?? null),
      }),
    );
  }, [
    nlFilters,
    nlApplied,
    nlBase,
    nlChanges,
    catalogue,
    allByKey,
    byKey,
    conditions,
    periode,
    periodeStart,
    periodeEnd,
    setConditions,
    onPeriodChange,
    onPeriodTokenChange,
    onSearchApplied,
  ]);

  /* Edited by hand since the search set it, the board no longer answers the search's account of it: its
     kept-and-changed banner goes, and the dashboard says so beside the explanation (`useSearchEdited`). */
  const searchEdited =
    nlApplied != null && selectionKey(conditions, { periode, periodeStart, periodeEnd }) !== nlApplied;
  // The search's own banners belong to a search in force.
  const shownDims = nlFilters ? searchedDims : [];
  const shownIgnored = nlFilters ? ignoredColumns : [];

  const onPick = useCallback(
    (dimension: string, value: string) => {
      if (!value) return;
      toggleCondition(dimension, value);
    },
    [toggleCondition],
  );

  const hasMe = !isGlobalView && (catalogue?.has_own ?? catalogue?.has_practitioner ?? true);
  const allPresets = useQuestionPresets(
    dimensions,
    backendScope === "specialite" ? "specialite" : "mine",
    hasMe,
  );
  const presets = useMemo(
    () => (comparesColleagues ? allPresets : allPresets.filter((preset) => preset.target === "composition")),
    [allPresets, comparesColleagues],
  );
  const compositionCard = cards.find((c) => c.id === COMPOSITION_ID);

  const applyPreset = useCallback(
    (preset: QuestionPreset) => {
      const target = preset.target === "composition" ? COMPOSITION_ID : SINGULARITY_ID;

      // Clicking the active chip again UNDOES it. A preset that can only be applied is a door with
      // no handle on the inside: the reader has no way back to the board they started from short of
      // guessing which chip was the default.
      if (activeQuestion === preset.id) {
        patchCard(COMPOSITION_ID, { dims: [], mode: "n", representation: null });
        patchCard(SINGULARITY_ID, { family: null });
        setActiveQuestion(null);
        return;
      }

      patchCard(target, preset.patch);
      setActiveQuestion(preset.id);
      // And take the reader TO the card the question just reconfigured. Two cards fit on a screen
      // today; the moment a third is pinned, "Qui sont mes patients ?" silently rewrites something
      // below the fold and the chip looks broken.
      window.requestAnimationFrame(() => {
        document
          .getElementById(`pathway-card-${target}`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    },
    [activeQuestion, patchCard],
  );

  /* Turn what is on screen into a question of one's own — which is all a pinned question is. */
  const pinCurrent = useCallback(
    (card: PathwayCard) => {
      const label =
        card.kind === "composition"
          ? (card.dims.length > 0 ? card.dims : compositionCard?.dims ?? [])
              .map((key) => byKey.get(key)?.label ?? key)
              .join(" × ")
          : t`Ce qui me distingue`;
      pinQuestion({ label, card: { ...card } });
    },
    [byKey, compositionCard, pinQuestion, t],
  );

  const activeConditionCount = useMemo(
    () => Object.values(conditions).reduce((sum, condition) => sum + conditionWeight(condition), 0),
    [conditions],
  );

  /* ONE reset, and it clears everything that can narrow this view: the pathway conditions, the
     period, and the search itself. Three mechanisms narrow the board and only one of them had a
     button, so a search that resolved to a period alone left "62 sur 240" with nothing to click. */
  // What the "Filtres" toggle counts: everything its rail can clear, which is not the search.
  const railFilterCount =
    activeConditionCount +
    (periode || periodeStart || periodeEnd ? 1 : 0) +
    (practitioner ? 1 : 0);
  const hasNarrowing =
    activeConditionCount > 0 ||
    Boolean(periode || periodeStart || periodeEnd) ||
    Boolean(nlFilters && nlFilters.length > 0) ||
    practitioner !== null;

  // Everything but the practitioner, who narrows the perimeter the total is counted in: "sur 40 —
  // tout afficher" must land on those 40, not on the whole perimeter.
  const resetSelection = useCallback(() => {
    setSearchedDims([]);
    setIgnoredColumns([]);
    clearConditions();
    onPeriodTokenChange?.(null);
    onPeriodChange?.(null, null);
    onResetAll?.();
  }, [clearConditions, onPeriodChange, onPeriodTokenChange, onResetAll]);

  const resetEverything = useCallback(() => {
    if (!scoped) setPractitionerFilter(null);
    resetSelection();
  }, [resetSelection, scoped, setPractitionerFilter]);

  // A range is one chip, read as « ≥ 75 » or « 17/01/2022 → 22/10/2026 »; `value` is null for it.
  const activeConditionChips = useMemo(
    () =>
      Object.entries(conditions).flatMap(([key, condition]): ConditionChip[] => {
        const label = axisLabel(key);
        if (isRange(condition)) {
          return [{ key, value: null, text: rangeText(condition, i18n.locale), label }];
        }
        return condition.map((value) => ({ key, value, text: labelOf(key, value), label }));
      }),
    [conditions, axisLabel, labelOf, i18n.locale],
  );
  const widenedLabels = (summary.data?.widened ?? []).map(axisLabel).join(", ");

  if (catalogueError) {
    return (
      <div className="px-6 py-16 text-center text-sm text-slate-500">
        <Trans>La vue exploration n'est pas ouverte à votre spécialité.</Trans>
      </div>
    );
  }

  // Nothing at all in the pathway layer — not "no patient matches", but "this patientèle has never
  // been built". The two are indistinguishable on a board that only ever draws counts, and reading
  // an unbuilt patientèle as an empty practice is the one mistake this view must not let a doctor
  // make. Told apart on the TOTAL rather than on the selection, so a filter that legitimately
  // matches nothing keeps saying so.
  if (summary.data && summary.data.total.episodes === 0) {
    // Narrowed to a practitioner, an empty total says nothing about the build — and this early return
    // takes the rail with it, so the way out has to be here.
    if (practitioner) {
      return (
        <div className="px-6 py-16 text-center text-sm text-slate-500">
          <p>
            <Trans>
              Aucun épisode pour{" "}
              <strong className="text-slate-700">{practitioner.label}</strong>{" "}
              sur ce périmètre.
            </Trans>
          </p>
          <button
            type="button"
            onClick={() => setPractitionerFilter(null)}
            className="mt-3 text-xs font-medium text-primary hover:underline"
          >
            <Trans>Retirer le filtre praticien</Trans>
          </button>
        </div>
      );
    }
    return (
      <div className="px-6 py-16 text-center text-sm text-slate-500">
        <Trans>
          Le parcours de soins n'a pas encore été construit pour cette patientèle. Cette vue restera
          vide jusque-là — ce n'est pas une patientèle sans épisode.
        </Trans>
      </div>
    );
  }

  return (
    /* The rail sits beside the WHOLE board, not beside the cards: the chips a tick adds above the cards
       pushed it down under the reader's cursor. */
    <div className="flex items-start gap-4 px-6 py-4">
      <div className="min-w-0 flex-1">
        {/* ── Counter, and what acts on the selection it counts ── */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="text-sm text-slate-700 tabular-nums">
            {/* Patients AND episodes, always both: a patient revised three years later is two
                episodes, and showing one number under the other's name is exactly the confusion this
                layer exists to end.

                The patient count is also the way in to the dossiers behind it — a click on the page
                filters the page, deliberately and always, and this is the separate gesture for seeing
                who is behind the bar. It carries no button of its own: see PathwayPatientsModal. */}
            {/* Read in aggregate, a count under the anonymity threshold comes back `null`: withheld,
                which is not the « … » of a counter still being read. */}
            <PathwayPatientsModal
              query={query}
              count={summary.data?.selection.patients ?? null}
              withheld={!!summary.data && summary.data.selection.patients === null}
              disabled={!summary.data?.names_patients}
              labelOf={labelOf}
              actKey={actKey}
            />
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="font-semibold text-slate-900">
              {summary.data ? (summary.data.selection.episodes ?? <WithheldCount />) : "…"}
            </span>{" "}
            <Trans>épisodes</Trans>
            {summary.data && summary.data.selection.patients !== summary.data.total.patients && (
              /* The way back is where the reader is already looking. "62 sur 240" is exactly the
                 moment they want the 240, so the 240 is the button. */
              <button
                type="button"
                onClick={resetSelection}
                className="ml-2 text-2xs text-slate-400 underline decoration-dotted underline-offset-2 hover:text-primary"
              >
                <Trans>sur {summary.data.total.patients ?? <WithheldCount />} — tout afficher</Trans>
              </button>
            )}
            {/* Nothing about practitioners here. A headcount beside a spread is one arithmetic step
                from naming the outlier on a small speciality, and naming the perimeter instead only
                repeated what the scope selector already says two inches above. */}
          </p>

          {/* Saving lived only in the header, behind a natural-language search — which was the only
              place a selection could be built before this rail existed. It is now built here, so it is
              saved here. */}
          {onSaveCohort && hasNarrowing && backendScope !== "specialite" && (
            <button
              type="button"
              onClick={() =>
                onSaveCohort({
                  patients: summary.data?.selection.patients ?? 0,
                  episodes: summary.data?.selection.episodes ?? 0,
                })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-primary/50 hover:text-primary"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              <Trans>Sauvegarder comme cohorte</Trans>
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            {actions}
            <button
              type="button"
              onClick={() => setRailOpen(!railOpen)}
              aria-expanded={railOpen}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                railOpen || railFilterCount > 0
                  ? "border-primary/50 bg-primary/5 text-primary"
                  : "border-slate-200 bg-white text-slate-600 hover:border-primary/50 hover:text-primary",
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              <Trans>Filtres</Trans>
              {railFilterCount > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-3xs font-bold tabular-nums text-white">
                  {railFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* A search names dimensions, and there are two useful things to do with them: narrow to the
            values it found, or look at those dimensions across the whole selection. It applies as a
            filter because that is what "search" means, and the other reading is one click away rather
            than a second search syntax to learn. */}
        {(shownDims.length > 0 ||
          shownIgnored.length > 0 ||
          unmatchedTerms ||
          (searchChanges && !searchEdited)) && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
            {/* What the search did to the board's own conditions, counted by the server: the model's
                explanation is a sentence, this is what happened (PT-146). */}
            {searchChanges && !searchEdited ? (
              <span className="text-2xs text-slate-600">{searchChanges}</span>
            ) : (
              shownDims.length > 0 && (
                <span className="text-2xs text-slate-600">
                  <Trans>
                    La recherche a été appliquée comme filtre sur{" "}
                    {shownDims.map(axisLabel).join(", ")}.
                  </Trans>
                </span>
              )
            )}
            {summary.data?.period_ignored && (
              /* The one filter whose failure is invisible: an unknown column leaves a name to report,
                 an unreadable date leaves nothing at all, and the board would show the whole cohort
                 under a question that named a year. */
              <span className="text-2xs font-medium text-slate-500">
                <Trans>La période demandée n'a pas été comprise — la sélection n'est pas restreinte dans le temps.</Trans>
              </span>
            )}
            {shownIgnored.length > 0 && (
              <span className="text-2xs font-medium text-slate-500">
                <Trans>
                  Non appliqué ici : {shownIgnored.join(", ")} — ces champs ne sont pas portés par la
                  couche parcours.
                </Trans>
              </span>
            )}
            {unmatchedTerms && (
              <span className="text-2xs font-medium text-slate-500">
                <Trans>
                  Sans valeur correspondante sur ce tableau de bord :{" "}
                  {unmatchedTerms}.
                </Trans>
              </span>
            )}
            {shownDims.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const dims = shownDims.slice(0, catalogue?.max_dimensions ?? 4);
                const next = { ...conditions };
                for (const key of shownDims) delete next[key];
                setConditions(next);
                /* FIRST, and then scrolled to. A card appended below two others answers the question
                   the reader just asked somewhere they are not looking, which reads as nothing having
                   happened. */
                const id = addCard({ kind: "composition", dims, mode: "n", representation: null }, "start");
                setSearchedDims([]);
                window.requestAnimationFrame(() => {
                  document
                    .getElementById(`pathway-card-${id}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                });
              }}
              className="text-2xs font-medium text-primary hover:underline"
            >
              <Trans>En faire une carte</Trans>
            </button>
            )}
            <button
              type="button"
              onClick={() => {
                setSearchedDims([]);
                setIgnoredColumns([]);
                setChangesShown(false);
              }}
              aria-label={t`Masquer cette suggestion`}
              className="ml-auto rounded p-0.5 text-slate-400 hover:bg-white hover:text-slate-600"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        <div data-tour="pathway-questions" className="mb-3">
          <QuestionBar
            presets={presets}
            pinned={pinned}
            activeId={activeQuestion}
            onApply={applyPreset}
            onApplyPinned={(question) =>
              patchCard(
                question.card.kind === "composition" ? COMPOSITION_ID : SINGULARITY_ID,
                question.card,
              )
            }
            onUnpin={unpinQuestion}
          />
        </div>

        {hasNarrowing && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {/* First, because it narrows the perimeter every other chip is read in. */}
            {practitioner && (
              <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 py-0.5 pl-2 pr-1 text-2xs text-slate-600">
                <span className="text-slate-400">
                  <Trans>Praticien</Trans>
                </span>
                <span className="font-medium text-primary">
                  {practitioner.label}
                </span>
                <button
                  type="button"
                  aria-label={t`Retirer ce filtre`}
                  onClick={() => setPractitionerFilter(null)}
                  className="rounded-full p-0.5 hover:bg-primary/10"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
            {activeConditionChips.map((chip) => (
              <span
                key={`${chip.key}:${chip.value ?? "plage"}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white py-0.5 pl-2 pr-1 text-2xs text-slate-600"
              >
                <span className="text-slate-400">{chip.label}</span>
                <span className="font-medium text-slate-800">{chip.text}</span>
                <button
                  type="button"
                  aria-label={t`Retirer ce filtre`}
                  onClick={() =>
                    chip.value === null ? setRange(chip.key, null) : toggleCondition(chip.key, chip.value)
                  }
                  className="rounded-full p-0.5 hover:bg-slate-100"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
            {(periode || periodeStart || periodeEnd) && (
              <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white py-0.5 px-2 text-2xs text-slate-600">
                {/* WHICH date it bites on, because the pathway holds two and they disagree on 12 %
                    of episodes. The fine-grained window filters the episode's OPENING, as the classic
                    dashboard's "période" always has; the rail carries both years as ordinary facets
                    for a reader who means the operation. */}
                <span className="text-slate-400">
                  <Trans>Vus pendant</Trans>
                </span>
                <span className="font-medium text-slate-800">
                  {periode ?? `${periodeStart ?? "…"} → ${periodeEnd ?? "…"}`}
                </span>
              </span>
            )}
            {widenedLabels && (
              /* Outside the reader's patientèle a range reads at the grain the board publishes there:
                 two counts a day apart would be one patient's date of surgery (PT-144). Said, because
                 the counts no longer answer the range to the day. */
              <span className="text-2xs text-slate-500">
                <Trans>
                  Hors de votre patientèle, {widenedLabels} : plage élargie à l'année ou à la tranche
                  entière.
                </Trans>
              </span>
            )}
            <button
              type="button"
              onClick={resetEverything}
              className="text-2xs font-medium text-slate-500 hover:text-primary"
            >
              <Trans>Tout effacer</Trans>
            </button>
          </div>
        )}

        <div className="space-y-4">
          {cards.map((card, index) => card.kind !== "composition" && !comparesColleagues ? null : (
            /* Drag by the handle only. A card is full of things a reader clicks — chips, bars,
               labels that filter the page — and making the whole surface draggable turns every
               mis-aimed click into a move. */
            <div
              key={card.id}
              onDragOver={(event) => {
                if (dragging === null || dragging === index) return;
                event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragging !== null) moveCard(dragging, index);
                setDragging(null);
              }}
              className={cn("relative", dragging === index && "opacity-50")}
            >
              <button
                type="button"
                draggable
                onDragStart={() => setDragging(index)}
                onDragEnd={() => setDragging(null)}
                aria-label={t`Déplacer la carte`}
                className="absolute -left-5 top-5 hidden cursor-grab rounded p-0.5 text-slate-300 hover:text-slate-500 active:cursor-grabbing lg:block"
              >
                <GripVertical className="h-4 w-4" />
              </button>
              {card.kind === "composition" ? (
              <CompositionCardBound
                card={card}
                query={query}
                catalogue={dimensions}
                maxDimensions={catalogue?.max_dimensions ?? 4}
                defaultDims={catalogue?.default_dimensions ?? []}
                onPick={onPick}
                onPatch={(patch) => {
                  patchCard(card.id, patch);
                  setActiveQuestion(null);
                }}
                onRemove={card.id === COMPOSITION_ID ? undefined : () => removeCard(card.id)}
                onPin={() => pinCurrent(card)}
              />
            ) : (
              <SingularityCardBound
                card={card}
                query={query}
                // Only a previewed global view is known here to have no "me": whether a reader is in the
                // distribution is the server's answer, read off their own dossiers.
                hasMe={!isGlobalView}
                labelOf={labelOf}
                onPatch={(patch) => {
                  patchCard(card.id, patch);
                  setActiveQuestion(null);
                }}
                onRemove={card.id === SINGULARITY_ID ? undefined : () => removeCard(card.id)}
              />
              )}
            </div>
          ))}

          {/* A card the reader builds is a composition. The gap card is one per board, always there, its
              family switched on the card itself: a second one only repeated its title. Nothing here adds
              a card the board already draws. */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-3">
            <span className="text-xs font-medium text-slate-500">
              <Trans>Ajouter une carte</Trans>
            </span>
            <DimensionPicker
              dimensions={dimensions}
              exclude={composedDims}
              label={<Trans>composition</Trans>}
              onPick={(key) => addCard(newComposition(key))}
            />
          </div>
        </div>
      </div>

      {railOpen && (
        <PathwayFilterRail
          facets={facets.data?.facets}
          perimeter={`${backendScope}:${cohortId ?? ""}:${datasetId ?? ""}:${practitioner?.id ?? ""}`}
          conditions={conditions}
          activeCount={railFilterCount}
          loading={facets.loading}
          periode={periode}
          periodeStart={periodeStart}
          periodeEnd={periodeEnd}
          // The token setter clears the range itself. Clearing it again here reset the token too:
          // since 28/08 no chip of the rail had narrowed anything.
          onPeriodToken={(token) => onPeriodTokenChange?.(token)}
          onPeriodRange={(start, end) => {
            onPeriodTokenChange?.(null);
            onPeriodChange?.(start, end);
          }}
          onToggle={toggleCondition}
          onRange={setRange}
          // The rail's count includes the period and the practitioner, so its reset clears them too.
          onClear={resetEverything}
          onClose={() => setRailOpen(false)}
          practitionerFilter={
            canFilterByPractitioner
              ? {
                  options: practitioners.data?.practitioners ?? [],
                  selected: practitioner,
                  onChange: setPractitionerFilter,
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

/** One chip of the active conditions: a ticked label, or a whole range (`value` null). */
interface ConditionChip {
  key: string;
  value: string | null;
  text: string;
  label: string;
}

/** Each card owns its own request, so one slow dimension never blocks the other card. */
function CompositionCardBound({
  card,
  query,
  catalogue,
  maxDimensions,
  defaultDims,
  onPick,
  onPatch,
  onRemove,
  onPin,
}: {
  card: PathwayCard;
  query: ReturnType<typeof buildPathwayQuery>;
  catalogue: PathwayDimension[];
  maxDimensions: number;
  defaultDims: string[];
  onPick: (dimension: string, value: string) => void;
  onPatch: (patch: Partial<PathwayCard>) => void;
  onRemove?: () => void;
  onPin: () => void;
}) {
  const { t } = useLingui();
  /* An empty card asks the server for its default pair (`_default_dims`) rather than for the
     catalogue's copy of it: waiting for the catalogue sent the card's request a second time, one
     round trip later, on every load of the board. */
  const { data, loading, error } = usePathwayComposition(query, card.dims);
  const dims =
    card.dims.length > 0
      ? card.dims
      : (data?.dimensions.map((d) => d.key) ?? defaultDims);
  const title =
    card.title ??
    (data && data.dimensions.length > 0
      ? data.dimensions.map((d) => d.label).join(" × ")
      : t`De quoi ma pratique est faite`);

  return (
    <CompositionCard
      id={`pathway-card-${card.id}`}
      data={data}
      loading={loading}
      error={error}
      title={title}
      dims={dims}
      mode={card.mode}
      representation={card.representation}
      maxDimensions={maxDimensions}
      catalogue={catalogue}
      onChangeDims={(next) => onPatch({ dims: next.length > 0 ? next : dims.slice(0, 1) })}
      onChangeMode={(mode) => onPatch({ mode })}
      onChangeRepresentation={(representation: Representation | null) => onPatch({ representation })}
      onPick={onPick}
      onRemove={onRemove}
      onPin={onPin}
    />
  );
}

function SingularityCardBound({
  card,
  query,
  hasMe,
  labelOf,
  onPatch,
  onRemove,
}: {
  card: PathwayCard;
  query: ReturnType<typeof buildPathwayQuery>;
  hasMe: boolean;
  labelOf: (dimension: string, value: string) => string;
  onPatch: (patch: Partial<PathwayCard>) => void;
  onRemove?: () => void;
}) {
  const { t } = useLingui();
  const { data, loading, error } = usePathwaySingularity(query, card.family ?? null);
  /* The TITLE follows whether there is a "me", not merely which mode came back. Fixing the subtitle
     and the legend and leaving this alone is why a global reader still read "Où je me situe" over a
     card that had correctly stopped situating them. */
  const dispersion = data?.mode === "dispersion";
  const inDistribution = hasMe && data?.has_me !== false;
  const title =
    card.title ??
    (dispersion
      ? inDistribution
        ? t`Ma position parmi les praticiens`
        : t`Les pratiques qui varient le plus entre praticiens`
      : t`Ce qui me distingue de mes confrères`);
  return (
    <SingularityCard
      id={`pathway-card-${card.id}`}
      data={data}
      loading={loading}
      error={error}
      title={title}
      hasMe={hasMe}
      family={card.family ?? null}
      onChangeFamily={(family) => onPatch({ family })}
      onRemove={onRemove}
      labelOf={labelOf}
    />
  );
}
